import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { StateCode, isValidStateCode } from "@shared/regions";
import { useAuth } from "./AuthContext";
import { apiRequest } from "@/lib/queryClient";

export type RadiusOption = "3" | "10" | "20" | "all";

interface RegionContextType {
  selectedRegion: StateCode;
  setSelectedRegion: (region: StateCode) => void;
  selectedSuburb: string | undefined;
  setSelectedSuburb: (suburb: string | undefined) => void;
  selectedRadius: RadiusOption;
  setSelectedRadius: (radius: RadiusOption) => void;
  isLoading: boolean;
  locationDialogOpen: boolean;
  setLocationDialogOpen: (open: boolean) => void;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

export function RegionProvider({ children }: { children: ReactNode }) {
  const { userProfile } = useAuth();
  const [selectedRegion, setSelectedRegionState] = useState<StateCode>("ALL");
  const [selectedSuburb, setSelectedSuburbState] = useState<string | undefined>(undefined);
  const [selectedRadius, setSelectedRadiusState] = useState<RadiusOption>("10");
  const [isLoading, setIsLoading] = useState(true);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  // Initialize region and suburb from user profile or localStorage
  useEffect(() => {
    const initializeRegion = async () => {
      try {
        // Priority 1: User profile (if logged in)
        if (userProfile?.location?.state && isValidStateCode(userProfile.location.state)) {
          setSelectedRegionState(userProfile.location.state as StateCode);
          setSelectedSuburbState(userProfile.location.suburb);
          setIsLoading(false);
          return;
        }

        // Fallback to old selectedRegion field
        if (userProfile?.selectedRegion && isValidStateCode(userProfile.selectedRegion)) {
          setSelectedRegionState(userProfile.selectedRegion as StateCode);
          setIsLoading(false);
          return;
        }

        // Priority 2: LocalStorage
        const savedRegion = localStorage.getItem("ozknews_region");
        const savedSuburb = localStorage.getItem("ozknews_suburb");
        const savedRadius = localStorage.getItem("ozknews_radius") as RadiusOption;
        if (savedRegion && isValidStateCode(savedRegion)) {
          setSelectedRegionState(savedRegion as StateCode);
          setSelectedSuburbState(savedSuburb || undefined);
        }
        if (savedRadius && ["3", "10", "20", "all"].includes(savedRadius)) {
          setSelectedRadiusState(savedRadius);
        }
        setIsLoading(false);
        return;
      } catch (error) {
        console.error("Failed to initialize region:", error);
        setSelectedRegionState("ALL");
      } finally {
        setIsLoading(false);
      }
    };

    initializeRegion();
  }, [userProfile]);

  const setSelectedRegion = async (region: StateCode) => {
    setSelectedRegionState(region);

    // Save to localStorage
    localStorage.setItem("ozknews_region", region);

    // Save to the real D1 account record if logged in. This used to call
    // userService.ts's updateUserProfile(), which writes to a Firestore
    // `users/{uid}` document that nothing else reads (AuthContext builds
    // userProfile from GET /api/me / D1, and getOrCreateUserProfile() that
    // would read that Firestore doc back is never called anywhere) - so the
    // region picked here never actually reached the account. /api/me/location
    // writes the real users.region_code column that /api/me and MyPage read.
    if (userProfile) {
      try {
        await apiRequest("POST", "/api/me/location", { regionCode: region });
      } catch (error) {
        console.error("Failed to save region to profile:", error);
      }
    }
  };

  const setSelectedSuburb = async (suburb: string | undefined) => {
    setSelectedSuburbState(suburb);

    // Save to localStorage
    if (suburb) {
      localStorage.setItem("ozknews_suburb", suburb);
    } else {
      localStorage.removeItem("ozknews_suburb");
    }
  };

  const setSelectedRadius = (radius: RadiusOption) => {
    setSelectedRadiusState(radius);
    localStorage.setItem("ozknews_radius", radius);
  };

  return (
    <RegionContext.Provider value={{ selectedRegion, setSelectedRegion, selectedSuburb, setSelectedSuburb, selectedRadius, setSelectedRadius, isLoading, locationDialogOpen, setLocationDialogOpen }}>
      {children}
    </RegionContext.Provider>
  );
}

export function useRegion() {
  const context = useContext(RegionContext);
  if (context === undefined) {
    throw new Error("useRegion must be used within a RegionProvider");
  }
  return context;
}
